import { type CSSProperties, type HTMLAttributes, type ReactNode } from 'react';

type BoxProps = {
  // Width props (grid-styled compatible)
  width?: number | string;

  // Margin props (grid-styled compatible)
  m?: number | string;
  mt?: number | string;
  mr?: number | string;
  mb?: number | string;
  ml?: number | string;
  mx?: number | string;
  my?: number | string;

  // Padding props (grid-styled compatible)
  p?: number | string;
  pt?: number | string;
  pr?: number | string;
  pb?: number | string;
  pl?: number | string;
  px?: number | string;
  py?: number | string;

  // Flexbox alignment props
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch';

  // Standard HTML props
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
} & HTMLAttributes<HTMLDivElement>; // Allow other standard HTML div props

// Helper function to convert width values to CSS class names
const getWidthClass = (width: number | string): string => {
  if (typeof width === 'string') {
    return ''; // Let CSS handle string values via inline styles
  }

  // Handle fractional widths (common grid-styled patterns)
  if (width === 1) {
    return 'box-width-full';
  }
  if (width === 1 / 2 || width === 0.5) {
    return 'box-width-1-2';
  }
  if (width === 1 / 3) {
    return 'box-width-1-3';
  }
  if (width === 2 / 3) {
    return 'box-width-2-3';
  }
  if (width === 1 / 4 || width === 0.25) {
    return 'box-width-1-4';
  }
  if (width === 2 / 4 || width === 0.5) {
    return 'box-width-2-4';
  }
  if (width === 3 / 4 || width === 0.75) {
    return 'box-width-3-4';
  }

  return ''; // Fallback to inline styles for other values
};

// Helper function to convert spacing values to CSS class names
const getSpacingClass = (type: string, value: number | string): string => {
  if (typeof value === 'string') {
    if (value === 'auto' && (type.includes('ml') || type.includes('mr'))) {
      return `box-${type}-auto`;
    }
    return ''; // Let CSS handle other string values via inline styles
  }

  // Convert numeric values to utility classes
  if (value >= 0 && value <= 4 && Number.isInteger(value)) {
    return `box-${type}-${value}`;
  }

  return ''; // Fallback to inline styles for other values
};

// Helper function to convert alignSelf to CSS class names
const getAlignSelfClass = (alignSelf: string): string => {
  switch (alignSelf) {
    case 'auto':
      return 'self-auto';
    case 'flex-start':
      return 'self-start';
    case 'flex-end':
      return 'self-end';
    case 'center':
      return 'self-center';
    case 'baseline':
      return 'self-baseline';
    case 'stretch':
      return 'self-stretch';
    default:
      return '';
  }
};

// Helper function to convert spacing values to inline styles (fallback)
const getSpacingValue = (value: number | string): string => {
  if (typeof value === 'string') {
    if (value === 'auto') {
      return 'auto';
    }
    return value;
  }
  return `${value}rem`;
};

export function Box(props: BoxProps) {
  const {
    width,
    m,
    mt,
    mr,
    mb,
    ml,
    mx,
    my,
    p,
    pt,
    pr,
    pb,
    pl,
    px,
    py,
    alignSelf,
    children,
    className,
    style,
    ...rest
  } = props;

  // Generate utility classes
  const utilityClasses = [
    width !== undefined && getWidthClass(width),
    m !== undefined && getSpacingClass('m', m),
    mt !== undefined && getSpacingClass('mt', mt),
    mr !== undefined && getSpacingClass('mr', mr),
    mb !== undefined && getSpacingClass('mb', mb),
    ml !== undefined && getSpacingClass('ml', ml),
    mx !== undefined && getSpacingClass('mx', mx),
    my !== undefined && getSpacingClass('my', my),
    p !== undefined && getSpacingClass('p', p),
    pt !== undefined && getSpacingClass('pt', pt),
    pr !== undefined && getSpacingClass('pr', pr),
    pb !== undefined && getSpacingClass('pb', pb),
    pl !== undefined && getSpacingClass('pl', pl),
    px !== undefined && getSpacingClass('px', px),
    py !== undefined && getSpacingClass('py', py),
    alignSelf !== undefined && getAlignSelfClass(alignSelf),
  ]
    .filter(Boolean)
    .join(' ');

  // Build fallback inline styles for values that don't have utility classes
  const fallbackStyle: CSSProperties = {
    // Width fallback
    ...(width !== undefined &&
      !getWidthClass(width) && {
        width: typeof width === 'string' ? width : `${width}rem`,
      }),

    // Margin fallbacks
    ...(m !== undefined && !getSpacingClass('m', m) && { margin: getSpacingValue(m) }),
    ...(mt !== undefined && !getSpacingClass('mt', mt) && { marginTop: getSpacingValue(mt) }),
    ...(mr !== undefined && !getSpacingClass('mr', mr) && { marginRight: getSpacingValue(mr) }),
    ...(mb !== undefined && !getSpacingClass('mb', mb) && { marginBottom: getSpacingValue(mb) }),
    ...(ml !== undefined && !getSpacingClass('ml', ml) && { marginLeft: getSpacingValue(ml) }),
    ...(mx !== undefined &&
      !getSpacingClass('mx', mx) && {
        marginLeft: getSpacingValue(mx),
        marginRight: getSpacingValue(mx),
      }),
    ...(my !== undefined &&
      !getSpacingClass('my', my) && {
        marginTop: getSpacingValue(my),
        marginBottom: getSpacingValue(my),
      }),

    // Padding fallbacks
    ...(p !== undefined && !getSpacingClass('p', p) && { padding: getSpacingValue(p) }),
    ...(pt !== undefined && !getSpacingClass('pt', pt) && { paddingTop: getSpacingValue(pt) }),
    ...(pr !== undefined && !getSpacingClass('pr', pr) && { paddingRight: getSpacingValue(pr) }),
    ...(pb !== undefined && !getSpacingClass('pb', pb) && { paddingBottom: getSpacingValue(pb) }),
    ...(pl !== undefined && !getSpacingClass('pl', pl) && { paddingLeft: getSpacingValue(pl) }),
    ...(px !== undefined &&
      !getSpacingClass('px', px) && {
        paddingLeft: getSpacingValue(px),
        paddingRight: getSpacingValue(px),
      }),
    ...(py !== undefined &&
      !getSpacingClass('py', py) && {
        paddingTop: getSpacingValue(py),
        paddingBottom: getSpacingValue(py),
      }),

    // Merge with provided style
    ...style,
  };

  // Remove CSS props from rest to prevent React 19 warnings
  const cssProps = [
    'width',
    'm',
    'mt',
    'mr',
    'mb',
    'ml',
    'mx',
    'my',
    'p',
    'pt',
    'pr',
    'pb',
    'pl',
    'px',
    'py',
    'alignSelf',
  ];
  const domProps = Object.fromEntries(Object.entries(rest).filter(([key]) => !cssProps.includes(key)));

  // Combine utility classes with user-provided className
  const finalClassName = [utilityClasses, className].filter(Boolean).join(' ');

  return (
    <div
      className={finalClassName || undefined}
      style={Object.keys(fallbackStyle).length > 0 ? fallbackStyle : undefined}
      {...domProps}
    >
      {children}
    </div>
  );
}
